import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Grid, Link as MuiLink, Paper, Skeleton, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { BenefitDTOBenefitEnum, BikeleasingNewsDTO, TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { StaticLinks, useStaticLinks } from 'probonio-shared-ui/module/links/hooks';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useBikeleasingPortalLinkBuilder } from 'probonio-shared-ui/utils/bikeleasingUrl';
import { normalizeTel, normalizeWa } from 'probonio-shared-ui/utils/normalize';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { PaperCard } from '../../component/card/PaperCard';
import { VerticalBox } from '../../component/layout/BoxLayout';
import { BIKELEASING_FAQ_URL } from '../../constants';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { useBikeleasingNews } from '../../module/benefits/bikeleasingV2/useBikeleasingNews';
import { useBikeleasingPrecondition } from '../../module/benefits/bikeleasingV2/useBikeleasingPrecondition';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';
import { colors } from '../../theme';

const CONTACT_CONFIG = {
  support: { phoneKey: 'BLS_OTHER_TOPICS_TEL', mailKey: 'BLS_INFO' },
  insurance: { phoneKey: 'BLS_BIKE_INSURANCE_TEL', mailKey: 'BLS_BIKE_INSURANCE_MAIL' },
  agv: { phoneKey: 'BLS_EMPLOYER_INSURANCE_TEL', mailKey: 'BLS_EMPLOYER_INSURANCE_MAIL' },
  buyout: { phoneKey: 'BLS_BUYOUT_TEL', mailKey: 'BLS_BUYOUT_MAIL' },
  dealerSupport: { phoneKey: 'BLS_DEALER_SUPPORT_TEL', mailKey: 'BLS_DEALER_SUPPORT_MAIL' },
  logistics: { phoneKey: 'BLS_LOGISTICS_TEL', mailKey: 'BLS_LOGISTICS_MAIL' },
} as const;

const CONTACT_KEYS = Object.keys(CONTACT_CONFIG) as (keyof typeof CONTACT_CONFIG)[];

function getSupportInformation(key: keyof typeof CONTACT_CONFIG, staticLinks: StaticLinks) {
  const config = CONTACT_CONFIG[key];
  return { phone: staticLinks[config.phoneKey], mail: staticLinks[config.mailKey] };
}

const NewsItem = ({ item }: { item: BikeleasingNewsDTO }) => {
  return (
    <Grid size={1}>
      <Link
        to={`/benefits/${BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase()}/articles/${item.uid}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <VerticalBox
          sx={{
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'grey.300',
            padding: 1,
            borderRadius: 1,
            '&:hover': { backgroundColor: colors.bg.default },
            height: '100%',
          }}
        >
          {item.image && (
            <Box
              component="img"
              src={item.image.url}
              title={item.image.title}
              alt={item.image.alternative}
              sx={{ maxHeight: 200, objectFit: 'cover', borderRadius: 0.5, width: '100%' }}
            />
          )}
          {item.published && (
            <Typography variant="body2" color={colors.text.secondary} marginTop={0.5}>
              {DateTime.fromISO(item.published.date).toLocaleString(DateTime.DATE_SHORT)}
            </Typography>
          )}
          <Typography variant="h6" marginTop={1}>
            {item.title}
          </Typography>
        </VerticalBox>
      </Link>
    </Grid>
  );
};

export function BikeleasingInformationPage() {
  const { t, i18n } = useTranslation('extensions');
  const langCode = (i18n.resolvedLanguage || 'de').toUpperCase().substring(0, 2);
  const staticLinks = useStaticLinks();
  const { tenant } = useTenant();
  const { PreconditionComponent, isPreconditionMet } = useBikeleasingPrecondition();
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  const bikeleasingLeasingCalculatorUrl = buildBikeleasingPortalLink(`/utils/leasing-calculator`);
  const bikeleasingDealerMapUrl = buildBikeleasingPortalLink(`/utils/retailer-search`);

  const pimCoreUrl = import.meta.env.VITE_BIKELEASING_PIM_URL;
  let insuranceUrl = `${pimCoreUrl}/get-asset/PDF/EN/Germany/insurance-and-service-manual-de-en.pdf`;
  if (tenant?.countryCode === TenantCountryCodeEnum.At) {
    insuranceUrl = `${pimCoreUrl}/get-asset/PDF/EN/Austria/insurance-and-service-manual-at-en.pdf`;
  } else if (langCode === 'DE') {
    insuranceUrl = `${pimCoreUrl}/get-asset/PDF/DE/Versicherungshandbuch_DE.pdf`;
  }

  const { data: bikeleasingNews, isLoading } = useBikeleasingNews();
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase();

  return (
    <AuthorizedLayout
      title={t('bikeleasing_v2.information.title')}
      breadcrumbs={[{ title: t('bikeleasing_v2.title'), to: `/benefits/${benefit}` }]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.BikeleasingV2)}
        title={t('bikeleasing_v2.title')}
        subTitle={t('bikeleasing_v2.information.title')}
      >
        {isPreconditionMet ? (
          <>
            <Grid container spacing={2} marginBottom={2}>
              {[
                { translationKey: 'leasingCalculator', url: bikeleasingLeasingCalculatorUrl },
                { translationKey: 'dealerMap', url: bikeleasingDealerMapUrl },
              ].map((item, index) => (
                <Grid size={6} key={item.translationKey}>
                  <PaperCard sx={{ height: '100%' }} title={t(`bikeleasing_v2.information.${item.translationKey}.title`)}>
                    <Grid
                      container
                      spacing={2}
                      direction={{ xs: 'column-reverse', lg: index % 2 === 0 ? 'row' : 'row-reverse' }}
                      height="100%"
                      justifyContent="flex-start"
                    >
                      <Grid size={{ xs: 12, lg: 6 }} container justifyContent="flex-end" flexGrow={1}>
                        <Typography variant="body1">{t(`bikeleasing_v2.information.${item.translationKey}.description`)}</Typography>
                        <Button
                          sx={{ alignSelf: 'flex-end' }}
                          variant="text"
                          endIcon={<OpenInNewIcon />}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t(`bikeleasing_v2.information.${item.translationKey}.buttonText`)}
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <Box
                          component="img"
                          src={`/image/benefits/bikeleasingV2/${item.translationKey}-information.jpg`}
                          alt={t(`bikeleasing_v2.information.${item.translationKey}.title`)}
                          style={{
                            width: '100%',
                            height: '100%',
                            maxHeight: 300,
                            aspectRatio: '4 / 3',
                            borderRadius: 8,
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                        />
                      </Grid>
                    </Grid>
                  </PaperCard>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} marginBottom={2}>
              {staticLinks && (
                <>
                  <Grid size={3}>
                    <PaperCard title={t('bikeleasing_v2.information.resources.title')} sx={{ height: '100%' }}>
                      <VerticalBox gap={1}>
                        {[
                          {
                            translationKey: 'insurance',
                            url: insuranceUrl,
                          },
                          {
                            translationKey: 'damage',
                            url: `${pimCoreUrl}/get-asset/PDF/${langCode}/ablauf_im_schadensfall.pdf`,
                          },
                          { translationKey: 'faq', url: BIKELEASING_FAQ_URL },
                          { translationKey: 'messages', url: `https://wa.me/${normalizeWa(staticLinks.BLS_WA)}` },
                        ].map(item => (
                          <Button
                            key={item.translationKey}
                            variant="outlined"
                            fullWidth
                            endIcon={<OpenInNewIcon />}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ height: 'fit-content', hyphens: 'auto' }}
                          >
                            {t(`bikeleasing_v2.information.resources.${item.translationKey}`)}
                          </Button>
                        ))}
                      </VerticalBox>
                    </PaperCard>
                  </Grid>
                  <Grid size={9}>
                    <PaperCard title={t('bikeleasing_v2.information.contacts.title')} sx={{ height: '100%' }}>
                      <Grid container spacing={2} sx={{ height: '100%', alignItems: 'stretch' }}>
                        {CONTACT_KEYS.map(contactKey => {
                          const contactInfo = getSupportInformation(contactKey, staticLinks);
                          return (
                            <Grid size={{ xs: 6, lg: 4 }} key={contactKey}>
                              <Paper
                                elevation={0}
                                sx={{
                                  backgroundColor: 'grey.200',
                                  padding: 1,
                                  textAlign: 'center',
                                  justifyContent: 'center',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%',
                                }}
                              >
                                <Typography variant="body1" color={colors.text.secondary}>
                                  {t(`bikeleasing_v2.information.contacts.${contactKey}`)}
                                </Typography>
                                <MuiLink href={`tel:${normalizeTel(contactInfo.phone)}`} variant="body2" fontWeight={500}>
                                  {contactInfo.phone}
                                </MuiLink>
                                <MuiLink href={`mailto:${contactInfo.mail}`} variant="body2" fontWeight={500}>
                                  {contactInfo.mail}
                                </MuiLink>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </PaperCard>
                  </Grid>
                </>
              )}
            </Grid>

            <PaperCard
              title={t('bikeleasing_v2.information.news.title')}
              headerActions={
                <Button component={Link} variant="text" size="small" to={`/benefits/${benefit}/articles`}>
                  {t('bikeleasing_v2.information.news.buttonText')}
                </Button>
              }
            >
              <Grid container spacing={2} columns={5}>
                {isLoading && (
                  <>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Grid key={index} size={1}>
                        <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 1 }} />
                      </Grid>
                    ))}
                  </>
                )}
                {!isLoading &&
                  bikeleasingNews?.totalCount &&
                  bikeleasingNews &&
                  bikeleasingNews.results.slice(0, 5).map(item => <NewsItem key={item.uid} item={item} />)}
                {!isLoading && !bikeleasingNews?.totalCount && (
                  <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ my: 4, mx: 'auto' }}>
                    {t('bikeleasing_v2.information.news.noData.list')}
                  </Typography>
                )}
              </Grid>
            </PaperCard>
          </>
        ) : (
          <BenefitPaper>{PreconditionComponent}</BenefitPaper>
        )}
      </BenefitSubPage>
    </AuthorizedLayout>
  );
}
